import React, { useState, useEffect, useCallback } from 'react';

interface BotOrder {
  id: string;
  character_id: string;
  character_name: string;
  order_type: 'buy' | 'sell';
  item_id: string;
  quantity: number;
  filled_quantity: number;
  price_per_unit: number;
  region: string;
  status: string;
  created_at: string;
}

interface BotOrdersDisplayProps {
  apiBaseUrl: string;
  adminKey: string;
}

export const BotOrdersDisplay: React.FC<BotOrdersDisplayProps> = ({ apiBaseUrl, adminKey }) => {
  const [orders, setOrders] = useState<BotOrder[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [autoRefresh, setAutoRefresh] = useState<boolean>(true);
  const [orderType, setOrderType] = useState<'all' | 'buy' | 'sell'>('all');

  const fetchOrders = useCallback(async (): Promise<void> => {
    try {
      setIsLoading(true);
      const response = await fetch(`${apiBaseUrl}/api/admin/prod/bot-orders`, {
        headers: { 'x-admin-key': adminKey },
      });
      if (response.ok) {
        const data = await response.json();
        setOrders(data.orders || []);
      }
    } catch (err) {
      console.error('Error fetching bot orders:', err);
    } finally {
      setIsLoading(false);
    }
  }, [apiBaseUrl, adminKey]);

  useEffect(() => {
    fetchOrders();
    if (!autoRefresh) return;
    const interval = setInterval(() => fetchOrders(), 5000);
    return () => clearInterval(interval);
  }, [fetchOrders, autoRefresh]);

  const filteredOrders = orders.filter(order => orderType === 'all' || order.order_type === orderType);

  const buyOrders = orders.filter(o => o.order_type === 'buy');
  const sellOrders = orders.filter(o => o.order_type === 'sell');
  const totalVolume = orders.reduce((sum, o) => sum + o.quantity, 0);
  const filledVolume = orders.reduce((sum, o) => sum + o.filled_quantity, 0);

  return (
    <div style={{
      backgroundColor: '#1a1a2e',
      border: '1px solid #4a5568',
      borderRadius: 0,
      padding: 16,
      marginBottom: 16,
      fontFamily: 'monospace',
    }}>
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: 16,
      }}>
        <h3 style={{ color: '#ffa500', fontSize: 14, margin: 0, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 1 }}>
          🤖 BOT ACTIVE ORDERS
        </h3>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <div style={{ display: 'flex', gap: 4 }}>
            {(['all', 'buy', 'sell'] as const).map(type => (
              <button
                key={type}
                onClick={() => setOrderType(type)}
                style={{
                  padding: '4px 8px',
                  backgroundColor: orderType === type ? '#ffa500' : '#2d3748',
                  color: orderType === type ? '#000' : '#e2e8f0',
                  border: 'none',
                  fontSize: 10,
                  cursor: 'pointer',
                  borderRadius: 0,
                  fontWeight: orderType === type ? 600 : 400,
                  textTransform: 'uppercase',
                  letterSpacing: 0.5,
                }}
              >
                {type === 'all' ? 'ALL' : type === 'buy' ? 'BUY' : 'SELL'}
              </button>
            ))}
          </div>
          <label style={{ display: 'flex', alignItems: 'center', gap: 6, color: '#a0aec0', fontSize: 11, cursor: 'pointer', fontFamily: 'monospace', textTransform: 'uppercase', letterSpacing: 0.5 }}>
            <input
              type="checkbox"
              checked={autoRefresh}
              onChange={(e) => setAutoRefresh(e.target.checked)}
              style={{ cursor: 'pointer' }}
            />
            AUTO-REFRESH
          </label>
          <button
            onClick={() => fetchOrders()}
            disabled={isLoading}
            style={{
              padding: '4px 8px',
              backgroundColor: isLoading ? '#2d3748' : '#4a5568',
              color: isLoading ? '#718096' : '#e2e8f0',
              border: '1px solid #4a5568',
              fontSize: 11,
              cursor: isLoading ? 'default' : 'pointer',
              borderRadius: 0,
              textTransform: 'uppercase',
              letterSpacing: 0.5,
              fontWeight: 600,
            }}
          >
            {isLoading ? 'LOADING...' : 'REFRESH'}
          </button>
        </div>
      </div>

      {/* Summary Stats */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(5, 1fr)',
        gap: 12,
        marginBottom: 16,
      }}>
        <div style={{
          backgroundColor: '#0b1220',
          border: '1px solid #4a5568',
          borderRadius: 0,
          padding: 12,
        }}>
          <div style={{ color: '#a0aec0', fontSize: 10, marginBottom: 4, textTransform: 'uppercase', letterSpacing: 0.5 }}>TOTAL ORDERS</div>
          <div style={{ color: '#3b82f6', fontSize: 16, fontWeight: 600 }}>{orders.length}</div>
        </div>
        <div style={{
          backgroundColor: '#0b1220',
          border: '1px solid #4a5568',
          borderRadius: 0,
          padding: 12,
        }}>
          <div style={{ color: '#a0aec0', fontSize: 10, marginBottom: 4, textTransform: 'uppercase', letterSpacing: 0.5 }}>BUY ORDERS</div>
          <div style={{ color: '#22c55e', fontSize: 16, fontWeight: 600 }}>{buyOrders.length}</div>
        </div>
        <div style={{
          backgroundColor: '#0b1220',
          border: '1px solid #4a5568',
          borderRadius: 0,
          padding: 12,
        }}>
          <div style={{ color: '#a0aec0', fontSize: 10, marginBottom: 4, textTransform: 'uppercase', letterSpacing: 0.5 }}>SELL ORDERS</div>
          <div style={{ color: '#ef4444', fontSize: 16, fontWeight: 600 }}>{sellOrders.length}</div>
        </div>
        <div style={{
          backgroundColor: '#0b1220',
          border: '1px solid #4a5568',
          borderRadius: 0,
          padding: 12,
        }}>
          <div style={{ color: '#a0aec0', fontSize: 10, marginBottom: 4, textTransform: 'uppercase', letterSpacing: 0.5 }}>TOTAL VOLUME</div>
          <div style={{ color: '#8b5cf6', fontSize: 16, fontWeight: 600 }}>{totalVolume}</div>
        </div>
        <div style={{
          backgroundColor: '#0b1220',
          border: '1px solid #4a5568',
          borderRadius: 0,
          padding: 12,
        }}>
          <div style={{ color: '#a0aec0', fontSize: 10, marginBottom: 4, textTransform: 'uppercase', letterSpacing: 0.5 }}>FILLED</div>
          <div style={{ color: '#fbbf24', fontSize: 16, fontWeight: 600 }}>{filledVolume}</div>
        </div>
      </div>

      {/* Orders Table */}
      <div style={{
        backgroundColor: '#0b1220',
        border: '1px solid #4a5568',
        borderRadius: 0,
        maxHeight: 400,
        overflowY: 'auto',
      }}>
        {filteredOrders.length === 0 ? (
          <div style={{
            padding: 16,
            color: '#718096',
            fontSize: 12,
            textAlign: 'center',
            textTransform: 'uppercase',
            letterSpacing: 0.5,
          }}>
            NO BOT ORDERS FOUND
          </div>
        ) : (
          <div>
            <div style={{
              display: 'grid',
              gridTemplateColumns: '120px 80px 80px 80px 80px 80px 100px 80px 140px',
              gap: 8,
              padding: '8px 12px',
              borderBottom: '1px solid #4a5568',
              backgroundColor: '#1a1a2e',
              fontSize: 10,
              fontWeight: 600,
              color: '#a0aec0',
              textTransform: 'uppercase',
              letterSpacing: 0.5,
              position: 'sticky',
              top: 0,
            }}>
              <div>BOT NAME</div>
              <div>TYPE</div>
              <div>ITEM</div>
              <div>QTY</div>
              <div>FILLED</div>
              <div>PRICE</div>
              <div>REGION</div>
              <div>STATUS</div>
              <div>CREATED</div>
            </div>
            {filteredOrders.map((order) => {
              const createdDate = new Date(order.created_at);
              const timeString = createdDate.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
              const dateString = createdDate.toLocaleDateString('en-US', { month: 'short', day: '2-digit' });
              return (
                <div
                  key={order.id}
                  style={{
                    display: 'grid',
                    gridTemplateColumns: '120px 80px 80px 80px 80px 80px 100px 80px 140px',
                    gap: 8,
                    padding: '8px 12px',
                    borderBottom: '1px solid #2d3748',
                    fontSize: 11,
                    color: '#e2e8f0',
                    alignItems: 'center',
                  }}
                >
                  <div style={{ color: '#ffa500', fontWeight: 600 }}>{order.character_name}</div>
                  <div style={{ color: order.order_type === 'buy' ? '#22c55e' : '#ef4444', fontWeight: 600 }}>
                    {order.order_type === 'buy' ? 'BUY' : 'SELL'}
                  </div>
                  <div>{order.item_id}</div>
                  <div>{order.quantity}</div>
                  <div style={{ color: '#fbbf24' }}>{order.filled_quantity}</div>
                  <div>₱{order.price_per_unit.toLocaleString()}</div>
                  <div style={{ color: '#a0aec0', fontSize: 10 }}>{order.region}</div>
                  <div style={{
                    color: order.status === 'active' ? '#22c55e' : '#718096',
                    fontWeight: 600,
                    textTransform: 'uppercase',
                    fontSize: 9,
                  }}>
                    {order.status}
                  </div>
                  <div style={{ color: '#a0aec0', fontSize: 10 }}>{dateString} {timeString}</div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};
